import { useMemo, useState } from 'react'

import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import useQueryGetPlayerPlaylog from '../../../../api/hooks/useQueryGetPlayerPlaylog'
import { playlogColumns } from './constants'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { Download, RotateCcw, Copy, ExternalLink } from 'lucide-react'
import { useMutationExportPlayerPlaylogCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerPlaylogCsv'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'
import useMutationReinitPlaylog from '../../../../api/hooks/useMutationReinitPlaylog'
import { GetPlayerPlaylogData } from '@/modules/manage-player/type'
import { ColumnDef } from '@tanstack/react-table'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/common/ui/dialog'
import { toast } from 'sonner'

interface PlaylogTableProps {
	playerId: string
}

export default function PlaylogTable({ playerId }: PlaylogTableProps) {
	const { pageSize, currentPage } = useTablePagination()
	const [sessionId, setSessionId] = useState<string | undefined>(undefined)
	const { dateFilter } = useDateFilter()
	const exportCsvMutation = useMutationExportPlayerPlaylogCsv()
	const [showExportModal, setShowExportModal] = useState(false)
	const [reinitResult, setReinitResult] = useState<{
		url: string
		html: string | null
	} | null>(null)

	const reinitMutation = useMutationReinitPlaylog({
		onSuccess: (data) => {
			setReinitResult({ url: data.url, html: data.html })
			toast.success('เปิดเกมสำเร็จ')
		},
		onError: () => {
			toast.error('ไม่สามารถเปิดเกมได้')
		},
	})

	const playlogPayload = {
		skip: (currentPage - 1) * pageSize,
		take: pageSize,
		...(sessionId && { sessionId }),
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}

	const { data: playlogData, isLoading: isPlaylogLoading } =
		useQueryGetPlayerPlaylog(playerId, playlogPayload)

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			playerId,
			startDate: dateFilter.startDate || undefined,
			endDate: dateFilter.endDate || undefined,
			sessionId,
			take: amount || playlogData?.total || 1000,
			skip: 0,
			filename: `player_playlog_${playerId}`,
			columnMappings: {
				provider: 'ผู้ให้บริการ',
				gameName: 'เกม',
				providerTransactionId: 'เลขจากผู้ให้บริการ',
				status: 'สถานะ',
				sessionId: 'เซสชั่น',
				info: 'ข้อมูล',
				walletType: 'กระเป๋า',
				transferIn: 'ยอดเดิมพัน',
				transferOut: 'ยอดจ่าย',
				balanceBefore: 'เงินคงเหลือก่อนเล่น',
				balanceAfter: 'เงินคงเหลือหลังเล่น',
				createdAt: 'วันที่',
				turnoverAfter: 'ยอดเทิร์นคงเหลือ',
			},
		})
	}

	const handleReinit = (initSessionLogId: string) => {
		reinitMutation.mutate({ playerId, initSessionLogId })
	}

	const columnsWithActions: ColumnDef<GetPlayerPlaylogData>[] = useMemo(
		() => [
			...playlogColumns,
			{
				id: 'actions',
				header: '',
				cell: ({ row }) => {
					const initSessionLogId = (
						row.original as GetPlayerPlaylogData & {
							initSessionLogId?: string | null
						}
					).initSessionLogId
					if (!initSessionLogId) return null
					return (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => handleReinit(initSessionLogId)}
							disabled={reinitMutation.isPending}
							title="เปิดเกมใหม่"
						>
							<RotateCcw className="h-4 w-4" />
						</Button>
					)
				},
			},
		],
		[reinitMutation.isPending],
	)

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-end gap-4">
				<Button
					variant="outline"
					onClick={() => setShowExportModal(true)}
					disabled={exportCsvMutation.isPending || !playlogData?.data?.length}
					className="flex items-center gap-2"
					size="sm"
				>
					<Download className="h-4 w-4" />
					โหลด CSV
				</Button>
				<DateRangePicker />
				<DateRangeButtons />
			</div>

			<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
				<div className="flex-1">
					<Input
						placeholder="ค้นหาเลขเซสชั่น"
						value={sessionId}
						onChange={(e) => setSessionId(e.target.value)}
						className="w-full sm:w-64"
					/>
				</div>
				<Button
					variant="outline"
					onClick={() => setSessionId('')}
					className="w-full sm:w-auto"
					size="sm"
				>
					ล้าง
				</Button>
			</div>

			<DataTablePaginate
				columns={columnsWithActions}
				data={playlogData?.data || []}
				totalItems={playlogData?.total || 0}
				isLoading={isPlaylogLoading}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={playlogData?.total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>

			<Dialog open={!!reinitResult} onOpenChange={() => setReinitResult(null)}>
				<DialogContent
					className={
						reinitResult?.html
							? 'max-w-screen flex h-screen max-h-screen w-screen flex-col gap-0 rounded-none border-0 p-0'
							: 'overflow-hidden'
					}
				>
					{reinitResult?.html ? (
						<>
							<div className="flex shrink-0 items-center justify-between border-b px-4 py-2">
								<span className="text-sm font-medium">
									เช็คประวัติการเล่นเกม
								</span>
							</div>
							<iframe
								srcDoc={reinitResult.html}
								className="h-full w-full flex-1 border-0"
								sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
								title="Game"
							/>
						</>
					) : (
						<>
							<DialogHeader>
								<DialogTitle>เปิดเกมสำเร็จ</DialogTitle>
								<DialogDescription>
									ลิงก์เกมที่สร้างใหม่สำหรับผู้เล่น
								</DialogDescription>
							</DialogHeader>
							{reinitResult?.url && (
								<div className="min-w-0 space-y-2 overflow-hidden">
									<div className="flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-md border p-3">
										<code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-sm">
											{reinitResult.url}
										</code>
										<Button
											variant="ghost"
											size="sm"
											className="shrink-0"
											onClick={() => {
												navigator.clipboard.writeText(reinitResult.url)
												toast.success('คัดลอกลิงก์แล้ว')
											}}
										>
											<Copy className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="shrink-0"
											onClick={() => window.open(reinitResult.url, '_blank')}
										>
											<ExternalLink className="h-4 w-4" />
										</Button>
									</div>
								</div>
							)}
							<DialogFooter>
								<Button variant="outline" onClick={() => setReinitResult(null)}>
									ปิด
								</Button>
							</DialogFooter>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	)
}
